<template>
  <div class="row">
    <div class="col-lg-6">
      <b-table :fields="userProposalTableFieldsBasicInfo"
               :items="[proposal]"
               class="table-responsive"
               head-variant="light"
               responsive
               striped>
        <template v-slot:cell(states.date)="row">
          {{ row.item.states.date | moment('YYYY-MM-DD HH:mm') }}
        </template>
        <template v-slot:cell(actions)="row">
          <action-download-pdf :disabled="false" :proposal="row.item"/>
          <action-download-report-pdf :proposal="row.item"/>
          <action-download-additional-sample-declaration-pdf v-if="isAdditionalSampleDeclaration(row.item)"
                                                             :proposal="row.item"/>
        </template>
      </b-table>

      <b-table :fields="userProposalTableFieldsExperimentInfo"
               :items="[proposal]"
               class="table-responsive"
               head-variant="light"
               responsive
               striped>
        <template v-slot:cell(experiment_color.evaluated_color)="row">
          <strong v-if="isExperimentColor(row.item)"
                  :class="'text-' + getExperimentColor(row.item.experiment_color.evaluated_color)">
            {{ getExperimentColorWithStatus(row.item.experiment_color.evaluated_color) }}
          </strong>
          <p v-else>-</p>
        </template>
        <template v-slot:cell(experiment_color.final_evaluated_color)="row">
          <strong v-if="isFinalExperimentColor(row.item)"
                  :class="'text-' + getExperimentColor(row.item.experiment_color.final_evaluated_color)">
            {{ getExperimentColorWithStatus(row.item.experiment_color.final_evaluated_color) }}
          </strong>
        </template>
      </b-table>

      <b-table :fields="userProposalTableFieldsTeam"
               :items="teamTableData"
               class="table-responsive"
               head-variant="light"
               responsive
               striped>
      </b-table>
      <proposal-details-modal-legend/>
    </div>

    <div class="col-lg-6">
      <div>
        <b-table :fields="userProposalTableFieldsEvaluation"
                 :items="evaluationTableData"
                 class="table-responsive"
                 head-variant="light"
                 responsive
                 striped
                 thead-class="table-header-hidden">
          <template v-slot:cell(evaluationComment)="row">
            <div v-if="isSafetyOrRadiationEvaluation(row.item)">
              <ul class="proposals-details-list">
                <li v-for="(comment, index) in row.item.evaluationComment" :key="index">{{ comment }}</li>
              </ul>
            </div>
            <div v-else>{{ row.item.evaluationComment }}</div>
          </template>
        </b-table>
      </div>
      <div v-if="isFinalExperimentColor(proposal)">
        <p class="mt-4">Additional sample declaration</p>
        <b-table :fields="asdTableFieldsEvaluation"
                 :items="asdTableData"
                 class="table-responsive"
                 head-variant="light"
                 responsive
                 striped
                 thead-class="table-header-hidden">
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
  import ActionDownloadPdf from '@/components/actions/ActionDownloadPdf';
  import ActionDownloadReportPdf from '@/components/actions/ActionDownloadReportPdf';
  import ActionDownloadAdditionalSampleDeclarationPdf
    from '@/components/actions/ActionDownloadAdditionalSampleDeclarationPdf';
  import trafficLightsOptions from '@/json/trafficLightsAcceptanceMarkSafety';
  import ProposalDetailsModalLegend from '@/components/proposal/ProposalDetailsModalLegend';

  export default {
    name: 'ProposalDetailsModal',
    components: {
      ActionDownloadAdditionalSampleDeclarationPdf,
      ActionDownloadReportPdf,
      ActionDownloadPdf,
      ProposalDetailsModalLegend,
    },
    props: {
      asdTableData: { type: Array },
      evaluationTableData: { type: Array },
      isCeric: {
        type: Boolean,
        default: false,
      },
      isRapidAccess: {
        type: Boolean,
        default: false,
      },
      proposal: { type: Object },
      teamTableData: { type: Array },
    },
    data() {
      return {
        userProposalTableFieldsBasicInfo: [
          {
            key: 'document_specification.document_id',
            label: 'Proposal number',
          },
          'title',
          {
            key: 'author',
            label: 'Main proposer',
            thStyle: { width: '7%' },
            class: 'custom-break-col-md',
          },
          {
            key: 'general.discipline',
            label: 'Discipline',
          },
          {
            key: 'general.end_station',
            label: 'End station',
          },
          {
            key: 'states.date',
            label: 'Submission date',
          },
          {
            key: 'actions',
            thStyle: { minWidth: '9rem' },
          },
        ],
        userProposalTableFieldsExperimentInfoCeric: [
          {
            key: 'final_grade.requested_hours',
            label: 'Requested number of hours',
          },
        ],
        userProposalTableFieldsExperimentInfoRapidAccess: [
          {
            key: 'general.shifts_minimal',
            label: 'Minimal requested number of shifts',
          },
          {
            key: 'general.shifts_optimal',
            label: 'Optimal requested number of shifts',
          },
        ],
        userProposalTableFieldsExperimentInfoStandardAndLongTerm: [
          {
            key: 'general.shifts',
            label: 'Requested number of shifts',
          },
        ],
        userProposalTableFieldsTeam: [
          {
            key: 'teamMember',
            label: 'Experimental team with roles',
          },
        ],
        userProposalTableFieldsEvaluation: [
          {
            key: 'evaluationType',
            class: 'column-header-style',
          },
          {
            key: 'evaluationComment',
            class: 'text-justify',
          },
          'evaluationResult',
        ],
        asdTableFieldsEvaluation: [
          {
            key: 'evaluationType',
            class: 'column-header-style',
          },
          {
            key: 'evaluationComment',
            class: 'text-justify',
          },
          'evaluationResult',
        ],
        trafficLightsOptions,
      };
    },
    computed: {
      userProposalTableFieldsExperimentInfo() {
        const standardColumns = [
          {
            key: 'evaluations_data.granted_shifts',
            label: 'Granted shifts',
          },
          {
            key: 'experiment_color.evaluated_color',
            label: 'Experiment color',
          },
          {
            key: 'evaluations_data.rank',
            label: 'Rank',
          },
        ];

        if (this.isFinalExperimentColor(this.proposal)) {
          standardColumns.splice(2, 0, {
            key: 'experiment_color.final_evaluated_color',
            label: 'Final experiment color',
          });
        }

        if (this.isCeric) {
          return [...this.userProposalTableFieldsExperimentInfoCeric, ...standardColumns.slice(0, -1)];
        }
        if (this.isRapidAccess) {
          return [...this.userProposalTableFieldsExperimentInfoRapidAccess, ...standardColumns.slice(0, -1)];
        }
        return [...this.userProposalTableFieldsExperimentInfoStandardAndLongTerm, ...standardColumns];
      },
    },
    methods: {
      isAdditionalSampleDeclaration(item) {
        return item.additional_sample_declaration?.states?.name === 'submitted';
      },
      getExperimentColor(status) {
        let variant = 'secondary';
        this.trafficLightsOptions.forEach((option) => {
          if (option.value === status) {
            variant = option.disabledVariant;
          }
        });
        return variant;
      },
      getExperimentColorWithStatus(status) {
        const experimentColor = this.trafficLightsOptions.find((option) => option.value === status);
        return `${experimentColor.color} - ${experimentColor.value}`;
      },
      isSafetyOrRadiationEvaluation(item) {
        return item.evaluationType === 'Safety evaluation' || item.evaluationType === 'Radiation evaluation';
      },
      isExperimentColor(item) {
        return item.experiment_color?.evaluated_color;
      },
      isFinalExperimentColor(item) {
        return item.experiment_color?.final_evaluated_color;
      },
    },
  };
</script>

<style scoped>
  ::v-deep .table-header-hidden {
    display: none;
  }

  ::v-deep .column-header-style {
    background-color: #e9ecef;
    width: 2rem;
  }

  .proposals-details-list {
    list-style-type: none;
    padding-left: 0;
  }
</style>
